/**
 * Background music. Browsers block autoplay until a user gesture, so we arm on
 * the first click/keypress and then honor play() requests. Two looping tracks:
 * an exploration theme for the menu and a driving track for gameplay. Press M
 * to mute. Built to grow later (per-scene tracks).
 */
const TRACKS = {
  menu: "assets/Where_the_Lost_Ones_Go.mp3",              // landing / menu theme
  game: "assets/ebunny-legend-acoustic-378544.mp3",        // dungeon / level music
  hub:  "assets/ebunny-simple-magic-acoustic-378635.mp3",  // Waystation / village music
  boss: "assets/topsecretmusicnetwork-dungeon-level-upbeat-power-music-160-bpm-293383.mp3",  // boss fights
};

// One-shot sound effects (in public/assets/sound/**). Add more freely — the
// footstep + weapon resolvers below map game state onto these logical names.
const SFX = {
  // footsteps
  step_dungeon: "assets/sound/steps/floraphonic-block-heel-footstep-2-184069.mp3",
  step_gravel:  "assets/sound/steps/freesound_community-knight-right-footstep-on-gravel-4-with-chainmail-101937.mp3",
  step_soft:    "assets/sound/steps/51124243-footstep-372877.mp3",
  // weapons
  swing:        "assets/sound/weapons/freesound_community-swinging-staff-whoosh-strong-08-44658.mp3",
  magic_charge: "assets/sound/weapons/fxprosound-magic-charge-mana-2-186628.mp3",
  magic_fire:   "assets/sound/weapons/yodguard-fire-magic-6-378641.mp3",
  magic_frost:  "assets/sound/weapons/yodguard-iced-magic-5-378605.mp3",
  magic_earth:  "assets/sound/weapons/yodguard-earth-magic-5-378598.mp3",
  magic_wind:   "assets/sound/weapons/yodguard-wind-magic-1-378628.mp3",
  magic_heal:   "assets/sound/weapons/yodguard-healing-magic-4-378668.mp3",
  // gag: banana jump scare
  banana_scream: "assets/banana-scream.mp3",
};

// per-biome footstep sound (falls back to the dungeon block-heel)
const STEP_BY_THEME = { dungeon: "step_dungeon", graveyard: "step_gravel", wilderness: "step_soft", hub: "step_soft" };
// per-element magic cast sound
const MAGIC_BY_ELEMENT = { fire: "magic_fire", frost: "magic_frost", poison: "magic_earth", shock: "magic_wind", arcane: "magic_charge" };

export class AudioManager {
  constructor() {
    const num = (k, d) => { try { const v = parseFloat(localStorage.getItem(k)); return isNaN(v) ? d : v; } catch (_) { return d; } };
    this.musicVol = num("relikara.vol.music", 0.6);   // master music volume (0..1)
    this.sfxVolume = num("relikara.vol.sfx", 0.7);    // master sfx volume (0..1)
    this._baseVol = {};
    this.audios = {};
    for (const [key, url] of Object.entries(TRACKS)) {
      const a = new Audio(url);
      a.loop = true;
      this._baseVol[key] = key === "menu" ? 0.55 : 0.7;   // per-track balance, scaled by musicVol
      a.volume = this._baseVol[key] * this.musicVol;
      this.audios[key] = a;
    }
    // preload one-shot SFX templates (cloned per play so they can overlap)
    this.sfxTemplates = {};
    for (const [key, url] of Object.entries(SFX)) {
      const a = new Audio(url); a.preload = "auto"; this.sfxTemplates[key] = a;
    }

    this.enabled = false;
    try { this.muted = localStorage.getItem("relikara.muted") === "1"; } catch (_) { this.muted = false; }
    this.current = null;
    this._want = "menu";   // landing plays the menu theme (Where the Lost Ones Go)

    const unlock = () => {
      this.enabled = true;
      if (this._want) this.play(this._want);
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    window.addEventListener("keydown", (e) => { if (e.key.toLowerCase() === "m") this.toggleMute(); });
  }

  play(which) {
    this._want = which;
    if (!this.enabled) return;
    const next = this.audios[which];
    if (!next) return;
    for (const [k, a] of Object.entries(this.audios)) if (k !== which) a.pause();
    if (this.current !== next) next.currentTime = 0;
    next.muted = this.muted;
    next.play().catch(() => {});
    this.current = next;
  }

  /** Fire a one-shot sound effect by logical name (see SFX). Overlaps freely. */
  sfx(name, vol = 1, rate = 1) {
    if (!this.enabled || this.muted) return;
    const tmpl = this.sfxTemplates[name];
    if (!tmpl) return;
    const node = tmpl.cloneNode();
    node.volume = Math.max(0, Math.min(1, this.sfxVolume * vol));
    node.playbackRate = rate;
    node.play().catch(() => {});
  }

  /** Footstep sound for the current biome (block-heel in the dungeon). Subtle. */
  footstep(themeId) {
    this.sfx(STEP_BY_THEME[themeId] || "step_dungeon", 0.17, 0.94 + Math.random() * 0.12);
  }

  /** Attack sound resolved from the weapon (elemental cast, or a swing whoosh). */
  weaponSound(weapon) {
    if (!weapon) return;
    if (weapon.magic || weapon.element) this.sfx(MAGIC_BY_ELEMENT[weapon.element] || "magic_charge", 0.6);
    else this.sfx("swing", 0.5, 0.93 + Math.random() * 0.14);
  }

  toggleMute() { this.setMuted(!this.muted); }

  _persist(k, v) { try { localStorage.setItem(k, String(v)); } catch (_) {} }

  setMuted(m) {
    this.muted = !!m;
    for (const a of Object.values(this.audios)) a.muted = this.muted;
    this._persist("relikara.muted", this.muted ? "1" : "0");
  }
  setMusicVolume(v) {
    this.musicVol = Math.max(0, Math.min(1, v));
    for (const [k, a] of Object.entries(this.audios)) a.volume = (this._baseVol[k] || 0.6) * this.musicVol;
    this._persist("relikara.vol.music", this.musicVol.toFixed(2));
  }
  setSfxVolume(v) {
    this.sfxVolume = Math.max(0, Math.min(1, v));
    this._persist("relikara.vol.sfx", this.sfxVolume.toFixed(2));
  }
  /** Play a short sample of the current sfx level (feedback while dragging the slider). */
  previewSfx() { this.sfx("magic_charge", 0.8); }
}
